import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, Output, SimpleChanges, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ToastBarService } from '../../../../services/toast-bar.service';

export interface LockInfoV2 {
  locked: boolean;
  mine: boolean;
  label: string;
}

const LANGUAGES = ['en', 'hr', 'de', 'slo', 'it'];

/**
 * Region 3 of the mockup: canvas toolbar (device toggle, lock status, Edit/View
 * toggle, language, Save). Lock/language/view-mode reuse the exact same state
 * and methods the existing #annotate-menu banner and language radios already
 * used (sendLockedBy(), onLanguageChange(), toogleDisableEdit()) - see
 * template-editor-v2.component.ts's lockInfoV2/toggleLockV2 getters/method.
 */
@Component({
  selector: 'app-channel-navbar-v2',
  imports: [CommonModule, FormsModule],
  templateUrl: './channel-navbar-v2.component.html',
  styleUrl: './channel-navbar-v2.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChannelNavbarV2Component implements OnChanges {
  @Input() isReadonlyMode = false;
  @Input() selectedLanguage = 'en';
  @Input() languageDisabled = false;
  @Input() lockInfo: LockInfoV2 | null = null;
  @Input() warningMessage: string | null = null;
  @Input() deviceMode: 'desktop' | 'mobile' = 'desktop';
  @Input() saveDisabled = false;
  @Input() saveLabel = 'Save';
  /** Disables only the "Edit" half of the toggle below - switching to "View"
   *  (readonly) stays available regardless of lock ownership. */
  @Input() editDisabled = false;

  @Input() reviewMode: 'off' | 'proof' | 'annotate' = 'off';
  @Input() shareUrl: string | null = null;
  /** False while the editor's undo stack is empty; disables the Undo button. */
  @Input() canUndo = false;
  /** False while the editor's redo stack is empty; disables the Redo button. */
  @Input() canRedo = false;

  @Output() setViewMode = new EventEmitter<boolean>();
  @Output() languageChange = new EventEmitter<string>();
  @Output() toggleLock = new EventEmitter<void>();
  @Output() refresh = new EventEmitter<void>();
  @Output() setDeviceMode = new EventEmitter<'desktop' | 'mobile'>();
  @Output() save = new EventEmitter<void>();
  @Output() setReviewMode = new EventEmitter<'off' | 'proof' | 'annotate'>();
  /** The editor owns the history and the blocks, so the navbar only reports the click. */
  @Output() undo = new EventEmitter<void>();
  /** Same as undo: the editor does the work. */
  @Output() redo = new EventEmitter<void>();

  readonly languages = LANGUAGES;

  protected shareOpen = false;

  constructor(private toast: ToastBarService, private host: ElementRef<HTMLElement>, private cdr: ChangeDetectorRef) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['reviewMode'] && this.reviewMode === 'off') {
      this.shareOpen = false;
    }
  }

  protected onEdit(): void {
    if (this.reviewMode !== 'off') {
      this.setReviewMode.emit('off');
    }
    this.setViewMode.emit(false);
  }

  protected onView(): void {
    this.setViewMode.emit(true);
  }

  protected onUndo(): void {
    this.undo.emit();
  }

  protected onRedo(): void {
    this.redo.emit();
  }

  protected openShare(): void {
    this.shareOpen = true;
    this.copyShareUrl();
  }

  protected copyShareUrl(): void {
    const url = this.shareUrl;
    if (!url) {
      return;
    }
    const done = (copied: boolean) => this.toast.showToastSuccess(copied ? 'Share link copied' : 'Share link ready');
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(url).then(() => done(true), () => done(false));
    } else {
      done(false);
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.shareOpen && this.host.nativeElement.querySelector('.cr-share-wrap')?.contains(event.target as Node)) {
      return;
    }
    if (this.shareOpen) {
      this.shareOpen = false;
      this.cdr.markForCheck();
    }
  }
}
